import { useMemo } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'

import { alpha } from '@mui/material/styles'
import { Box, Link as MuiLink, Avatar, useTheme } from '@mui/material'
import { CalculCodeStateKeys } from '@batical/types/src'
import { CALCUL_SPEC } from 'src/routes/calculSpecs'
import { useAuth } from 'src/contexts/AuthContext'
import { doc } from 'firebase/firestore'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { firestore } from 'src/shims/firebase'
import { isTeamFeatureEnabled } from 'src/utils/featureFlags'

const NAV_HEIGHT = 56

export function DashboardLayout() {
  const { user } = useAuth()
  const theme = useTheme()
  const { pathname } = useLocation()

  const calculPathSet = useMemo(
    () => new Set(CALCUL_SPEC.map(s => s.path as CalculCodeStateKeys)),
    []
  )

  const dashboardTail = pathname.startsWith('/dashboard/') ? pathname.slice('/dashboard/'.length) : ''
  const firstDashboardSeg = dashboardTail.split('/')[0] ?? ''
  const isSectionsNav = pathname.startsWith('/dashboard/sections')
  const isCalculsNav =
    pathname === '/dashboard/calculs' ||
    pathname === '/dashboard/calculs-par-domaine' ||
    calculPathSet.has(firstDashboardSeg as CalculCodeStateKeys)
  const isSauvegardesNav =
    pathname.startsWith('/dashboard/load') || pathname.startsWith('/dashboard/save')

  const isSubscriptionNav = pathname.startsWith('/subscription')
  const isTeamNav = pathname.startsWith('/team')

  const uid = user?.uid ?? ''
  const [groupSub] = useDocumentData(uid ? doc(firestore, 'subscription', uid, 'groupSubscription', 'data') : null)
  const groupSubscribers = (groupSub as any)?.subscribers as Array<{ active?: boolean }> | undefined
  const hasTeam = Array.isArray(groupSubscribers) && groupSubscribers.some(s => s.active !== false)

  const secondaryNavSx = {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    px: 1.5,
    py: 0.85,
    borderRadius: 1.5,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(66, 119, 185, 0.08)',
      color: theme.palette.primary.main
    }
  } as const

  const secondaryNavSxActive = (active: boolean) =>
    ({
      ...secondaryNavSx,
      ...(active && {
        color: theme.palette.primary.main,
        fontWeight: 700
      })
    }) as const

  const coreNavSx = (active: boolean) => ({
    textDecoration: 'none',
    fontWeight: 800,
    fontSize: { xs: '0.95rem', sm: '1.08rem' },
    letterSpacing: '-0.02em',
    px: { xs: 1.35, sm: 1.85 },
    py: { xs: 0.9, sm: 1 },
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    '&:hover': {
      color: theme.palette.primary.main
    }
  })

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          minHeight: NAV_HEIGHT,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, sm: 3 }
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            rowGap: 1
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 0, flex: { xs: '1 1 100%', md: '0 1 auto' } }}>
            <MuiLink
              component={Link}
              to="/dashboard"
              sx={{
                textDecoration: 'none',
                fontWeight: 800,
                fontSize: { xs: '1.2rem', sm: '1.3rem' },
                color: theme.palette.primary.main,
                letterSpacing: '-0.5px',
                flexShrink: 0,
                mr: { xs: 1.5, sm: 2 }
              }}>
              BatiKal
            </MuiLink>

            <Box
              aria-label="Navigation principale calculs"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.35, sm: 0.75 },
                pl: { xs: 1.5, sm: 2 },
                ml: { xs: 0.5, sm: 0 },
                borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                minHeight: 36
              }}>
              <MuiLink component={Link} to="/dashboard/sections" sx={coreNavSx(isSectionsNav)}>
                Sections
              </MuiLink>
              <MuiLink component={Link} to="/dashboard/calculs" sx={coreNavSx(isCalculsNav)}>
                Calculs
              </MuiLink>
              <MuiLink component={Link} to="/dashboard/load/calcul" sx={coreNavSx(isSauvegardesNav)}>
                Sauvegardes
              </MuiLink>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: { xs: 0.35, sm: 0.5 },
              flexWrap: 'wrap',
              flex: { xs: '1 1 100%', md: '0 0 auto' }
            }}>
            <MuiLink component={Link} to="/subscription" sx={secondaryNavSxActive(isSubscriptionNav)}>
              Abonnement
            </MuiLink>
            {isTeamFeatureEnabled() && (hasTeam || isTeamNav) && (
              <MuiLink component={Link} to="/team" sx={secondaryNavSxActive(isTeamNav)}>
                Mon équipe
              </MuiLink>
            )}
            <MuiLink component={Link} to="/mail-profile" sx={secondaryNavSx}>
              PDF & e-mail
            </MuiLink>
            <MuiLink
              component={Link}
              to="/profile"
              sx={{ ...secondaryNavSx, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.75rem',
                  bgcolor: theme.palette.primary.main
                }}>
                {(user?.email?.[0] ?? 'U').toUpperCase()}
              </Avatar>
              Profil
            </MuiLink>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 } }}>
        <Outlet />
      </Box>
    </Box>
  )
}
